import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import { Search } from "lucide-react";

const FLAGS = { pf: ["Yes", "No"], esi: ["Auto", "Yes", "No"], exg: ["Auto", "Yes", "No"], pt: ["Auto", "Yes", "No"] };

const EmpSearch = ({ value, onChange, count, total, testid }) => (
  <div className="flex flex-wrap items-center gap-3">
    <div className="relative w-full sm:w-80">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input className="h-10 pl-9" placeholder="Search employee by name or code" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
    {value && <span className="text-sm text-slate-600" data-testid={`${testid}-count`}>Showing {count} of {total}</span>}
  </div>
);

export default function Payroll() {
  const { actingOrgId } = useAuth();
  return (
    <div>
      <PageHeader title="Payroll" description="Design salary structures and run monthly payroll. All figures come from the engine." />
      <Tabs defaultValue="structure">
        <TabsList className="mb-5">
          <TabsTrigger value="structure" data-testid="tab-structure">Structure designer</TabsTrigger>
          <TabsTrigger value="run" data-testid="tab-run">Monthly run</TabsTrigger>
        </TabsList>
        <TabsContent value="structure"><Designer key={actingOrgId} /></TabsContent>
        <TabsContent value="run"><MonthlyRun key={actingOrgId} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Designer() {
  const { actingOrgId } = useAuth();
  const [config, setConfig] = useState(null);
  const [rows, setRows] = useState([]);
  const [results, setResults] = useState(null);
  const [totals, setTotals] = useState(null);
  const [checks, setChecks] = useState({});
  const [sid, setSid] = useState("");
  const [name, setName] = useState("Salary structure");
  const [status, setStatus] = useState("draft");
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [q, setQ] = useState("");
  const match = (r) => !q || `${r.employee_code} ${r.name} ${r.designation || ""}`.toLowerCase().includes(q.toLowerCase());

  const { data: employees = [] } = useQuery({ queryKey: ["pay-emps", actingOrgId], queryFn: async () => (await api.get("/org/employees")).data });
  const { data: masters } = useQuery({ queryKey: ["pay-masters", actingOrgId], queryFn: async () => {
    const [l] = await Promise.all([api.get("/org/masters/locations")]);
    return { locations: Object.fromEntries(l.data.map((x) => [x.id, x.state_code])) };
  }});

  useEffect(() => {
    api.get("/payroll/default-config").then((r) => setConfig(r.data));
    api.get("/exports/templates").then((r) => setTemplates(r.data.filter((t) => t.target === "zoho_payroll_salary")));
  }, [actingOrgId]);

  useEffect(() => {
    if (!employees.length || !masters) return;
    setRows(employees.map((e) => ({
      employee_code: e.employee_code || e.id.slice(0, 6), name: e.name, designation: e.designation_name || "",
      state_code: masters.locations[e.location_id] || "", date_of_joining: e.date_of_joining || "",
      monthly_gross: "", pf_flag: "Yes", esi_flag: "Auto", exgratia_flag: "Auto", pt_flag: "Auto",
      pt_override: "", tds_pm: "0", lwf_pm: "0",
    })));
  }, [employees, masters]);

  const setCell = (i, k, v) => setRows(rows.map((r, x) => (x === i ? { ...r, [k]: v } : r)));

  const importXlsx = async (file) => {
    const XLSXrows = await file.arrayBuffer().then(async () => {
      const fd = new FormData(); fd.append("file", file);
      // reuse master bulk reader is not applicable; parse client-side via SheetJS not available — rely on columns via simple CSV
      return null;
    });
    toast.message("Tip: paste gross values directly into the grid; xlsx import expects columns employee_code, monthly_gross, flags.");
  };

  const calculate = async () => {
    try {
      const emps = rows.filter((r) => r.monthly_gross).map((r) => ({ ...r, monthly_gross: Number(r.monthly_gross),
        tds_pm: Number(r.tds_pm || 0), lwf_pm: Number(r.lwf_pm || 0),
        pt_override: r.pt_override === "" ? null : Number(r.pt_override) }));
      const { data } = await api.post("/payroll/structure/calculate", { config, employees: emps });
      setResults(data.results); setTotals(data.totals);
      const ch = {}; data.results.forEach((r) => (r.checks || []).forEach((c) => (ch[c] = ch[c] || []).push(r.employee_code)));
      setChecks(ch);
      toast.success(`Calculated ${data.count} employees`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const save = async (submit) => {
    try {
      const emps = rows.filter((r) => r.monthly_gross).map((r) => ({ ...r, monthly_gross: Number(r.monthly_gross),
        tds_pm: Number(r.tds_pm || 0), lwf_pm: Number(r.lwf_pm || 0), pt_override: r.pt_override === "" ? null : Number(r.pt_override) }));
      const { data } = await api.post("/payroll/structures", { name, config, employees: emps, structure_id: sid || null });
      setSid(data.id); setStatus(data.status);
      if (submit) { await api.post(`/payroll/structures/${data.id}/submit`); setStatus("submitted"); toast.success("Submitted to Infiblu (frozen)"); }
      else toast.success("Draft saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const dl = async (url, fname, body) => {
    const res = await api.request({ url, method: body ? "post" : "get", data: body, responseType: "blob" });
    const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = fname; a.click(); URL.revokeObjectURL(u);
  };

  const num = (v) => formatINR(v);
  const C = (r, n) => (r.components.find((c) => c.name === n) || {}).amount;

  return (
    <div className="space-y-4">
      <Card className="flex flex-wrap items-center gap-3 p-4">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="max-w-xs" data-testid="structure-name" />
        <span className="text-xs text-slate-400">Status: {status}{sid ? ` · ${sid.slice(0, 8)}` : ""}</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button onClick={calculate} data-testid="calculate-btn">Calculate</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={status !== "draft"} data-testid="save-draft-btn">Save draft</Button>
          <Button variant="outline" onClick={() => save(true)} disabled={!sid || status !== "draft"} data-testid="submit-btn">Submit to Infiblu</Button>
        </div>
      </Card>

      {config && (
        <Card className="p-4 text-xs text-slate-500">
          <p className="font-medium text-slate-700">Config (editable dates)</p>
          <div className="mt-2 flex flex-wrap gap-3">
            <label>As on <Input className="mt-1 h-8 w-32" value={config.general.as_on_date || ""} data-testid="cfg-as-on"
              onChange={(e) => setConfig({ ...config, general: { ...config.general, as_on_date: e.target.value } })} /></label>
            <label>Structure effective <Input className="mt-1 h-8 w-32" value={config.general.structure_effective_date || ""} data-testid="cfg-eff"
              onChange={(e) => setConfig({ ...config, general: { ...config.general, structure_effective_date: e.target.value } })} /></label>
          </div>
          <p className="mt-2">{config.gratuity.note}</p>
        </Card>
      )}

      <EmpSearch value={q} onChange={setQ} count={rows.filter(match).length} total={rows.length} testid="designer-search" />
      <Card className="max-h-[70vh] overflow-auto p-0">
        <table className="w-full text-xs" data-testid="input-grid">
          <thead className="sticky top-0 z-10 bg-slate-100 text-slate-700 shadow-sm">
            <tr>{["Code", "Name", "State", "Gross", "PF", "ESI", "ExG", "PT", "PT ovr", "TDS", "LWF"].map((h) => <th key={h} className="px-2 py-2 text-left">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => match(r) && (
              <tr key={i} className="border-t border-slate-100" data-testid={`input-row-${r.employee_code}`}>
                <td className="px-2 py-1">{r.employee_code}</td><td className="px-2 py-1">{r.name}</td><td className="px-2 py-1">{r.state_code}</td>
                <td className="px-1"><Input className="h-7 w-20" value={r.monthly_gross} onChange={(e) => setCell(i, "monthly_gross", e.target.value)} data-testid={`gross-${r.employee_code}`} /></td>
                {[["pf_flag", "pf"], ["esi_flag", "esi"], ["exgratia_flag", "exg"], ["pt_flag", "pt"]].map(([k, g]) => (
                  <td key={k} className="px-1"><select className="h-7 rounded border text-xs" value={r[k]} onChange={(e) => setCell(i, k, e.target.value)}>
                    {FLAGS[g].map((o) => <option key={o}>{o}</option>)}</select></td>
                ))}
                <td className="px-1"><Input className="h-7 w-16" value={r.pt_override} onChange={(e) => setCell(i, "pt_override", e.target.value)} /></td>
                <td className="px-1"><Input className="h-7 w-16" value={r.tds_pm} onChange={(e) => setCell(i, "tds_pm", e.target.value)} /></td>
                <td className="px-1"><Input className="h-7 w-16" value={r.lwf_pm} onChange={(e) => setCell(i, "lwf_pm", e.target.value)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {results && (
        <>
          {Object.keys(checks).length > 0 && (
            <Card className="p-3 text-xs" data-testid="checks-panel">
              <p className="font-medium text-amber-700">Checks</p>
              {Object.entries(checks).map(([k, v]) => <p key={k}>{k}: {v.join(", ")}</p>)}
            </Card>
          )}
          <Card className="max-h-[70vh] overflow-auto p-0">
            <table className="w-full text-xs" data-testid="results-grid">
              <thead className="sticky top-0 z-10 bg-slate-100 text-slate-700 shadow-sm">
                <tr>{["Code", "Name", "Basic", "HRA", "Conv", "Lunch", "Special", "Grat/pm", "ER EPF", "ER EPS", "ER ESI", "Monthly CTC", "ExGratia pa", "Annual CTC", "EE PF", "EE ESI", "PT", "Net", "Grat payable"].map((h) => <th key={h} className="whitespace-nowrap px-2 py-2 text-right first:text-left">{h}</th>)}</tr>
              </thead>
              <tbody>
                {results.filter(match).map((r) => (
                  <tr key={r.employee_code} className="border-t border-slate-100 text-right" data-testid={`result-row-${r.employee_code}`}>
                    <td className="px-2 py-1 text-left">{r.employee_code}</td><td className="whitespace-nowrap px-2 py-1 text-left">{r.name}</td>
                    <td className="px-2">{num(C(r, "Basic"))}</td><td className="px-2">{num(C(r, "HRA"))}</td>
                    <td className="px-2">{num(C(r, "Conveyance"))}</td><td className="px-2">{num(C(r, "Lunch allowance"))}</td>
                    <td className="px-2">{num(C(r, "Special allowance"))}</td><td className="px-2">{num(r.gratuity_accrual_pm)}</td>
                    <td className="px-2">{num(r.er_epf)}</td><td className="px-2">{num(r.er_eps)}</td><td className="px-2">{num(r.er_esi)}</td>
                    <td className="px-2 font-medium">{num(r.monthly_ctc)}</td><td className="px-2">{num(r.ex_gratia_pa)}</td><td className="px-2">{num(r.annual_ctc)}</td>
                    <td className="px-2">{num(r.ee_pf)}</td><td className="px-2">{num(r.ee_esi)}</td><td className="px-2">{num(r.pt)}</td>
                    <td className="px-2 font-medium">{num(r.net_take_home)}</td><td className="px-2">{num(r.gratuity_payable_now)}</td>
                  </tr>
                ))}
                {totals && (
                  <tr className="border-t-2 border-slate-300 bg-slate-50 text-right font-semibold" data-testid="totals-row">
                    <td className="px-2 py-1 text-left" colSpan={11}>TOTAL (all employees)</td>
                    <td className="px-2">{num(totals.monthly_ctc)}</td><td className="px-2">{num(totals.ex_gratia_pa)}</td><td className="px-2">{num(totals.annual_ctc)}</td>
                    <td className="px-2">{num(totals.ee_pf)}</td><td className="px-2">{num(totals.ee_esi)}</td><td className="px-2">{num(totals.pt)}</td>
                    <td className="px-2">{num(totals.net_take_home)}</td><td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
          <Card className="flex flex-wrap items-center gap-3 p-4">
            <Button variant="outline" disabled={!sid} onClick={() => dl(`/payroll/structures/${sid}/export/excel`, "payroll_structure.xlsx")} data-testid="export-excel">Export Excel</Button>
            <Select value={tplId} onValueChange={setTplId}>
              <SelectTrigger className="w-72" data-testid="zoho-template"><SelectValue placeholder="Zoho payroll salary template" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" disabled={!sid || !tplId} onClick={() => dl(`/payroll/structures/${sid}/export/zoho`, "zoho_payroll_salary.xlsx", { template_id: tplId })} data-testid="export-zoho">Export to Zoho</Button>
            <span className="text-xs text-slate-400">Save the structure first to enable exports.</span>
          </Card>
        </>
      )}
    </div>
  );
}

function MonthlyRun() {
  const { actingOrgId } = useAuth();
  const [structures, setStructures] = useState([]);
  const [sid, setSid] = useState("");
  const [structure, setStructure] = useState(null);
  const [year, setYear] = useState("2026");
  const [month, setMonth] = useState("9");
  const [ceiling, setCeiling] = useState(null);
  const [rows, setRows] = useState([]);
  const [out, setOut] = useState(null);
  const [q, setQ] = useState("");
  const match = (r) => !q || `${r.employee_code} ${r.name}`.toLowerCase().includes(q.toLowerCase());

  useEffect(() => { api.get("/payroll/structures").then((r) => setStructures(r.data.filter((s) => ["submitted", "approved"].includes(s.status)))); }, [actingOrgId]);
  useEffect(() => {
    if (month && year) api.get(`/payroll/month-ceiling?year=${year}&month=${month}`).then((r) => setCeiling(r.data));
  }, [month, year]);

  const pick = async (id) => {
    setSid(id);
    const { data } = await api.get(`/payroll/structures/${id}`);
    setStructure(data);
    setRows(data.results.map((r) => ({ employee_code: r.employee_code, name: r.name, lop_days: "0",
      other_earnings: "0", other_deductions: "0", tds_pm: String(r.tds_pm || "0"), lwf_pm: String(r.lwf_pm || "0") })));
    setOut(null);
  };
  const setCell = (i, k, v) => setRows(rows.map((r, x) => (x === i ? { ...r, [k]: v } : r)));

  const run = async (final) => {
    try {
      const { data } = await api.post("/payroll/runs", { structure_id: sid, run_year: Number(year), run_month: Number(month),
        rows: rows.map((r) => ({ ...r, lop_days: Number(r.lop_days || 0), other_earnings: Number(r.other_earnings || 0),
          other_deductions: Number(r.other_deductions || 0), tds_pm: Number(r.tds_pm || 0), lwf_pm: Number(r.lwf_pm || 0) })), final });
      setOut(data);
      toast.success(final ? "Run saved (final)" : "Run calculated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const num = (v) => formatINR(v);

  return (
    <div className="space-y-4">
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <div><label className="text-xs">Structure</label>
          <Select value={sid} onValueChange={pick}>
            <SelectTrigger className="w-64" data-testid="run-structure"><SelectValue placeholder="Approved / submitted" /></SelectTrigger>
            <SelectContent>{structures.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.status})</SelectItem>)}</SelectContent>
          </Select></div>
        <div><label className="text-xs">Month</label><Input className="h-9 w-16" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="run-month" /></div>
        <div><label className="text-xs">Year</label><Input className="h-9 w-20" value={year} onChange={(e) => setYear(e.target.value)} data-testid="run-year" /></div>
      </Card>

      {ceiling && (
        <Card className="flex flex-wrap items-center gap-4 p-4 text-sm" data-testid="run-summary">
          <span>Days: <b>{ceiling.days_in_month}</b></span>
          <span>PF ceiling: <b>{num(ceiling.pf_ceiling)}</b></span>
          {ceiling.changeover && <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700" data-testid="changeover-badge">changeover month</span>}
          {out?.totals && <>
            <span className="ml-auto">Net payable: <b>{num(out.totals.net_payable)}</b></span>
            <span>Employer cost: <b>{num(out.totals.employer_cost)}</b></span>
            <span>Salary not paid: <b>{num(out.totals.salary_not_paid)}</b></span>
          </>}
        </Card>
      )}

      {structure && <EmpSearch value={q} onChange={setQ} count={rows.filter(match).length} total={rows.length} testid="run-search" />}
      {structure && (
        <Card className="max-h-[70vh] overflow-auto p-0">
          <table className="w-full text-xs" data-testid="run-grid">
            <thead className="sticky top-0 z-10 bg-slate-100 text-slate-700 shadow-sm"><tr>{["Code", "Name", "LOP", "Other earn", "Other ded", "TDS", "LWF"].map((h) => <th key={h} className="px-2 py-2 text-left">{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => match(r) && (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-2 py-1">{r.employee_code}</td><td className="px-2 py-1">{r.name}</td>
                  {["lop_days", "other_earnings", "other_deductions", "tds_pm", "lwf_pm"].map((k) => (
                    <td key={k} className="px-1"><Input className="h-7 w-20" value={r[k]} onChange={(e) => setCell(i, k, e.target.value)} data-testid={`${k}-${r.employee_code}`} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {structure && (
        <div className="flex gap-2">
          <Button onClick={() => run(false)} data-testid="run-calc">Calculate run</Button>
          <Button variant="outline" onClick={() => run(true)} data-testid="run-final">Save final</Button>
        </div>
      )}

      {out?.results && (
        <Card className="max-h-[70vh] overflow-auto p-0">
          <table className="w-full text-xs" data-testid="run-results">
            <thead className="sticky top-0 z-10 bg-slate-100 text-slate-700 shadow-sm"><tr>{["Code", "Name", "Earned gross", "Run PF wage", "EE PF", "EE ESI", "PT", "Net payable", "Employer cost", "Salary not paid"].map((h) => <th key={h} className="px-2 py-2 text-right first:text-left">{h}</th>)}</tr></thead>
            <tbody>
              {out.results.filter(match).map((r) => (
                <tr key={r.employee_code} className="border-t border-slate-100 text-right" data-testid={`run-result-${r.employee_code}`}>
                  <td className="px-2 py-1 text-left">{r.employee_code}</td><td className="whitespace-nowrap px-2 py-1 text-left">{r.name}</td>
                  <td className="px-2">{num(r.earned_gross)}</td><td className="px-2">{num(r.run_pf_wage)}</td><td className="px-2">{num(r.ee_pf)}</td>
                  <td className="px-2">{num(r.ee_esi)}</td><td className="px-2">{num(r.pt)}</td><td className="px-2 font-medium">{num(r.net_payable)}</td>
                  <td className="px-2">{num(r.employer_cost)}</td><td className="px-2">{num(r.salary_not_paid)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
